import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { initEventSync, shutdownEventSync } from './services/eventSync';
import { probeMaintenance } from './services/api';
import { useIsAdmin } from './hooks/useIsAdmin';
import { DayTrackingProvider } from './context/DayTrackingContext';
import { OdometerProvider } from './context/OdometerContext';
import { ManualVisitProvider } from './context/ManualVisitContext';
import { PullToRefreshProvider } from './context/PullToRefreshContext';
import Login from './pages/Login';
import Today from './pages/Today';
import Schedule from './pages/Schedule';
import MyRoutes from './pages/Routes';
import Sets from './pages/Sets';
import SetDetail from './pages/SetDetail';
import CallDetail from './pages/CallDetail';
import TimeTracking from './pages/TimeTracking';
import Profile from './pages/Profile';
import ProfileVerify from './pages/ProfileVerify';
import Resources from './pages/Resources';
import StoreDetail from './pages/StoreDetail';
// Editing an existing visit is ManualCallEntry's job. EditVisitPage is only
// the Sync-Issues fix-and-resubmit screen (#2082) — see its route below.
import Expenses from './pages/Expenses';
import Team from './pages/Team';
import Help from './pages/Help';
import OrderEntry from './pages/OrderEntry';
import ManualEntryPreflight from './pages/ManualEntryPreflight';
import ScanUpc from './pages/ScanUpc';
import GpsDiagnostics from './pages/GpsDiagnostics';
import SendDiagnostics from './pages/SendDiagnostics';
import Diagnostics from './pages/Diagnostics';
import DiagnosticsScanner from './pages/DiagnosticsScanner';
import DebugSync from './pages/DebugSync';
import BottomNav from './components/BottomNav';
import OfflineIndicator from './components/OfflineIndicator';
import UpdatePrompt from './components/UpdatePrompt';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import DevEnvBanner from './components/DevEnvBanner';
import { SyncIssuesBanner } from './components/SyncIssuesBanner';
import ErrorBoundary from './components/ErrorBoundary';
import SyncIssues from './pages/SyncIssues';
import EditVisitPage from './pages/EditVisitPage';
import { useSyncEngine } from './hooks/useSyncEngine';

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
    </div>
  );
}

// Protected route wrapper
/** #2025: does the rep need to verify/update their profile? (login gate signal) */
function profileVerificationRequired(): boolean {
  try {
    return !!(JSON.parse(localStorage.getItem('profileVerification') || 'null') as { required?: boolean } | null)
      ?.required;
  } catch {
    return false;
  }
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // #2025 profile-verification gate: route to the verify screen when required.
  // ONLINE-ONLY and it never traps the verify page itself — if offline we skip
  // (defer to the next online login) so a rep is never blocked from field work.
  if (
    profileVerificationRequired() &&
    typeof navigator !== 'undefined' &&
    navigator.onLine &&
    location.pathname !== '/profile/verify'
  ) {
    return <Navigate to="/profile/verify" replace />;
  }

  return <>{children}</>;
}

// Admin-only route wrapper — used for debug/diagnostic pages.
// Hides from non-admin users without exposing a 403; a direct URL visit
// silently redirects home, so regular reps can't wander in.
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { isAdmin, isChecking } = useIsAdmin();

  if (isLoading || isChecking) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Public route wrapper (redirect to home if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Layout with bottom navigation. Global overlays (offline indicator,
// update prompt, maintenance) are mounted at the AppRoutes level instead
// of here so they're available on EVERY route - not just the ones that
// happen to use AppLayout. #1823
function AppLayout({ children }: { children: React.ReactNode }) {
  // The bottom nav renders on every route, store detail included. Hiding it
  // there stranded a rep mid-visit: no way to reach More for the planogram, and
  // no way back (#3517, reported as E23). The page already reserves the nav's
  // height with pb-24, so the space was being held for a bar that never
  // rendered.
  //
  // Leaving is safe by design — a visit lives in IndexedDB, and Today shows an
  // "Active Call" banner that returns straight to it (return-to-active.spec.ts).
  return (
    // overflow-x-clip (not overflow-x-hidden) so this div doesn't become
    // an implicit vertical scroll container and trap wheel/touch events
    // before they reach the document scroll root. #1829 follow-up.
    <div className="w-full max-w-full overflow-x-clip">
      {children}
      <BottomNav />
    </div>
  );
}

// Singleton mounter for the rep activity event sync engine (#1950).
// We gate startup on `isAuthenticated` so the very first drain never
// fires against a missing JWT (which would 401-spam and bump retry
// counters uselessly).
function EventSyncMounter() {
  const { isAuthenticated, isLoading } = useAuth();
  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      initEventSync();
    } else {
      shutdownEventSync();
    }
  }, [isAuthenticated, isLoading]);
  return null;
}

// Always-on offline-first sync engine (#1983). Mounted only when the
// user is authenticated so the initial drain doesn't 401-spam.
function ActiveSyncEngine() {
  useSyncEngine();
  return null;
}

function SyncEngineMounter() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading || !isAuthenticated) return null;
  return <ActiveSyncEngine />;
}

function AppRoutes() {
  // Boot-time maintenance probe (#2135): the static maintenance page's
  // retry reload gets intercepted by the service worker, which serves the
  // cached app shell — so during a deploy the rep lands on a login page
  // with no signal that maintenance is running until they try something.
  // One no-preflight probe on boot raises the overlay immediately when the
  // gate is up; outside maintenance it's a single feather-weight GET.
  useEffect(() => {
    void probeMaintenance();
  }, []);

  return (
    <>
      {/* #2803 DEV/testing indicator (bright yellow banner + once-per-session
          modal + yellow PWA theme-color). Renders null in production. Mounted
          first so it shows on every route, above all other overlays. */}
      <DevEnvBanner />
      {/* Global overlays - mounted on every route so they work regardless
          of which layout wrapper a route uses. Previously these lived
          inside AppLayout and silently skipped Call/Set/Login/etc. #1823 */}
      {/* Bottom banners stack instead of overlapping at one fixed slot (diag
          #43: the dead Sync-Now banner hid the update banner behind it). The
          wrapper is pointer-events-none so its empty area never blocks taps;
          each banner re-enables pointer events on itself. */}
      <div className="pointer-events-none fixed bottom-20 left-4 right-4 z-50 flex flex-col gap-2">
        <SyncIssuesBanner />
        <UpdatePrompt />
        <OfflineIndicator />
      </div>
      <MaintenanceOverlay />
      <EventSyncMounter />
      <SyncEngineMounter />
      <ErrorBoundary label="routes">
      <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* #2025 profile-verification gate — full-screen, no bottom nav. The
          ProtectedRoute gate skips this path so it can't redirect to itself. */}
      <Route
        path="/profile/verify"
        element={
          <ProtectedRoute>
            <ProfileVerify />
          </ProtectedRoute>
        }
      />

      {/* Protected routes with bottom nav */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Today />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Schedule />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Redirect old /week route to /schedule */}
      <Route path="/week" element={<Navigate to="/schedule" replace />} />
      <Route
        path="/routes"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MyRoutes />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sets"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Sets />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/set/:setId"
        element={
          <ProtectedRoute>
            <SetDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/call/:callId"
        element={
          <ProtectedRoute>
            <CallDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/time"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TimeTracking />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Profile />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Resources />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Expenses />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Team />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Store detail - no bottom nav */}
      <Route
        path="/store/:storeId"
        element={
          <ProtectedRoute>
            {/* Wrapped like every other route so the bottom nav renders during a
                visit (#3517). This route rendered StoreDetail bare, which is why
                a rep working a call could not reach More for the planogram — and
                why the pathname check in AppLayout was dead code: the layout was
                never in play here at all. */}
            <AppLayout>
              <StoreDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Consolidated manual entry pre-flight (#1863). Lands in the canonical
          StoreDetail flow with ManualVisitContext set. Coexists with the old
          ManualCallEntry modal on the Time Sheet tab until Phase 3 (#1865). */}
      <Route
        path="/manual-entry/new"
        element={
          <ProtectedRoute>
            <ManualEntryPreflight />
          </ProtectedRoute>
        }
      />

      {/* Edit visit route removed - now using ManualCallEntry modal for editing */}

      {/* Help is a browse/reference page — keep the tab bar so it isn't a dead-end (#3372). */}
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Help />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Order entry - no bottom nav */}
      <Route
        path="/order"
        element={
          <ProtectedRoute>
            <OrderEntry />
          </ProtectedRoute>
        }
      />

      {/* UPC Scanner - no bottom nav */}
      <Route
        path="/scan-upc"
        element={
          <ProtectedRoute>
            <ScanUpc />
          </ProtectedRoute>
        }
      />

      {/* Diagnostics hub + sub-tools — #1873 (consolidates #1818, #1847, #1871/#1872) */}
      <Route
        path="/diagnostics"
        element={
          <ProtectedRoute>
            <Diagnostics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/diagnostics/scanner"
        element={
          <ProtectedRoute>
            <DiagnosticsScanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/diagnostics/gps"
        element={
          <ProtectedRoute>
            <GpsDiagnostics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/diagnostics/quick"
        element={
          <ProtectedRoute>
            <SendDiagnostics />
          </ProtectedRoute>
        }
      />

      {/* Legacy redirects — preserve any bookmarks / support links to the old standalone paths */}
      <Route path="/gps-diagnostics" element={<Navigate to="/diagnostics/gps" replace />} />
      <Route path="/send-diagnostics" element={<Navigate to="/diagnostics/quick" replace />} />

      {/* Admin-only sync diagnostics — #1471 */}
      <Route
        path="/debug/sync"
        element={
          <AdminRoute>
            <DebugSync />
          </AdminRoute>
        }
      />

      <Route
        path="/sync-issues"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SyncIssues />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* #2082 fix-and-resubmit: reopen a failed check-in as a draft to correct
          the offending field and resubmit. Full-screen (own header/footer). */}
      <Route
        path="/edit-visit/draft/:queueRowId"
        element={
          <ProtectedRoute>
            <EditVisitPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      </ErrorBoundary>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      {/* Odometer wraps day tracking: a drive leg stamps the last known
          odometer reading as it opens and closes, so the reading has to be
          in scope above it. Nothing in the odometer reads day state. */}
      <OdometerProvider>
        <DayTrackingProvider>
          <ManualVisitProvider>
            {/* #2089: one app-shell pull-to-refresh gesture + indicator; pages
                opt in with usePageRefresh(handler). */}
            <PullToRefreshProvider>
              <AppRoutes />
            </PullToRefreshProvider>
          </ManualVisitProvider>
        </DayTrackingProvider>
      </OdometerProvider>
    </AuthProvider>
  );
}

export default App;
